import type { AudioPlaybackDescriptor } from '@/composables/api/useAudioFileIssuesApi';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';

/** Which audio stream an entry plays, mirrored onto the bar as `activeDescriptorSource`. */
export type PlaybackSourceSlug = 'live_mp3' | 'live_hls' | 'backup_mp3' | 'ddex_mp3' | 'manual_mp3';

/**
 * How to obtain audio for a queue entry — the discriminator the store branches
 * on when it needs a (fresh) descriptor.
 *
 * The split is by *re-resolvability*, not by track type, because that is the
 * question every consumer actually asks:
 *
 *  - `live_track` / `raw_ddex` can be re-resolved, so an expired presigned URL
 *    self-heals on retry and the entry survives a page reload.
 *  - `fixed_descriptor` is a descriptor someone already fetched and handed us
 *    (the Audio File Issues inspector does this for a DDEX match row). Its URL
 *    expires and cannot be re-minted from what we hold, so it is neither
 *    retryable nor persistable.
 *  - `local_file` is a File the user picked but has not uploaded. It exists only
 *    in this tab's memory.
 */
export type QueueIdentity =
    | { kind: 'live_track'; code: string }
    | { kind: 'raw_ddex'; rawDdexTrackId: number }
    | { kind: 'fixed_descriptor'; descriptor: AudioPlaybackDescriptor }
    | { kind: 'local_file'; file: File };

export type QueueIdentityKind = QueueIdentity['kind'];

/** off → repeat the whole queue → repeat the current track. */
export type RepeatMode = 'off' | 'all' | 'one';

/**
 * How many entries a queue may hold, client-side.
 *
 * The server owns the cap on what a play-in-context request RETURNS
 * (`config/track_list_queue.php`, echoed as `meta.max_entries`). This is the
 * separate ceiling on what the client will let a queue grow to by appending,
 * and what it will restore from an old snapshot. One constant rather than three
 * literals, so the two can be reconciled in one place if they ever diverge.
 */
export const QUEUE_MAX_ENTRIES = 500;

/**
 * The records the player bar's three identity lines stand for, so a listener can
 * go from "what am I hearing?" to the track, artist or album without hunting for
 * the row again.
 *
 * Not to be confused with {@link QueueIdentity}, which is about obtaining AUDIO.
 * This is about navigating to RECORDS: an entry can be perfectly playable and
 * still have nothing to link to.
 *
 * Every field is nullable because call sites differ in what they hold. A track
 * card knows all four; an audio-file-issues row knows the track and its artist
 * but only the album's name; a locally uploaded file knows none. A line with no
 * id renders as the plain text it always was.
 */
export interface QueueEntryRecordLinks {
    /** Entity the title line opens — 'LiveTrack' or 'RawDdexTrack'. */
    trackEntityId: string | null;
    /** That entity's record id. NOT `code`, which is the audio key. */
    trackRecordId: number | string | null;
    artistId: number | string | null;
    albumId: number | string | null;
}

export interface QueueEntry extends QueueEntryRecordLinks {
    /**
     * Identity of this OCCURRENCE, not of the track.
     *
     * A generated playlist may name the same track three times, and those are
     * three rows you can remove and reorder independently — so the queue is
     * keyed by this, never by `code`. It is also what the prefetch slot is
     * keyed on, which is what makes a missed invalidation a leak rather than a
     * wrong track.
     */
    entryId: string;
    identity: QueueIdentity;
    /** What the bar reports as the active source while this entry plays. */
    source: PlaybackSourceSlug;
    /** Display/identity cache — becomes `trackCodeUpper`. Upper-cased. */
    code: string;
    title: string | null;
    artist: string | null;
    album: string | null;
    coverUrl: string | null;
    /**
     * The 1-based numbering of the playlist this entry came from, or null for a
     * list that has none (a search result set, a bucket, a one-off play).
     *
     * It exists because `code` does not identify a row: a generated playlist may
     * name the same track three times. When the player adopts a list around the
     * track you clicked, this is what says WHICH of the three you clicked.
     */
    position: number | null;
    /**
     * Set when this entry failed to play, after the one automatic retry. The
     * queue view renders it; the player stops rather than advancing past it.
     */
    failureReason: string | null;
    /**
     * The full track row behind this entry, when there is one — what lets the
     * queue render the SAME {@link TrackCard} a track list renders, at the same
     * three densities, instead of a bare row that shows less about the same
     * track.
     *
     * Null is a normal, permanent state, not a gap to be filled later. It means
     * "this entry did not come from the track-list-queue endpoint", which covers
     * every entry that reaches the queue by another route: a Shift-enqueue from
     * Track Matching, a one-off diagnostics play, a local file, a DDEX row. The
     * queue falls back to its plain row for those — there is nothing to fetch,
     * because those surfaces are not lists.
     *
     * Persisted, minus its signed cover URL — see `PersistedQueueEntry`. It is
     * plain metadata that does not expire, and leaving it out of the snapshot
     * made a page reload downgrade every row in the queue to the bare fallback
     * with no way back.
     */
    card: PlaylistTrackRow | null;
}

/**
 * Can we mint a fresh descriptor for this entry from what we hold?
 *
 * Drives both the automatic retry (an expired presigned URL is the boring,
 * self-healing case) and persistence (an entry we cannot re-resolve is dead
 * weight after a reload).
 */
export function canReresolveIdentity(identity: QueueIdentity): boolean {
    return identity.kind === 'live_track' || identity.kind === 'raw_ddex';
}

/**
 * Can we offer "View in Audio File Issues" for this entry?
 *
 * Only live tracks: Audio File Issues is keyed on a LiveTrack code, so there is
 * nothing to deep-check a DDEX track or a local file against.
 */
export function supportsDeepCheck(identity: QueueIdentity): boolean {
    return identity.kind === 'live_track';
}

/** Same rule as re-resolvability: if we cannot rebuild it, we must not store it. */
export function isPersistableIdentity(identity: QueueIdentity): boolean {
    return canReresolveIdentity(identity);
}

/**
 * Do two references describe the same queue OCCURRENCE?
 *
 * Codes must match. Positions decide only when BOTH sides carry one — and that
 * asymmetry is load-bearing, not laziness. A restored queue has no positions
 * (`PersistedQueueEntry` does not store them, and `restoreFromPersistence`
 * stamps `null`), and most surfaces never had one: the player bar, the Audio
 * File Issues inspector, a search row, a bucket row. Requiring strict equality
 * would make the row that IS playing offer "Play" after any reload, which is
 * exactly the behaviour the second half of the F5 review probe pins.
 *
 * Matching on `code` alone is what this replaces: a generated playlist can name
 * the same track three times, so every row for it claimed to be the one playing
 * — clicking a later occurrence paused instead of jumping, and Option-clicking
 * it re-sourced the earlier one.
 *
 * NOT used by `playListInContext`'s adoption search, deliberately: that is a
 * search with a drift fallback (position first, then code), not a boolean
 * identity test, and folding the fallback in here would make this predicate lie.
 */
export function isSameQueueOccurrence(
    left: { code: string; position: number | null },
    right: { code: string; position: number | null }
): boolean {
    if (left.code.toUpperCase() !== right.code.toUpperCase()) {
        return false;
    }

    if (left.position === null || right.position === null) {
        return true;
    }

    return left.position === right.position;
}
